import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'

import { trackClientEvent } from 'analytics/analytics'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import { EmptyArray } from 'lib/array/arrayUtils'
import { setManySearchParamValues } from 'lib/url/searchUrlUtils'
import { SEARCH_VERTICAL_TAB_MESSAGES, type SearchVertical } from './SearchVerticalTabs.config'
import {
  getActiveSearchVerticalTab,
  getSearchParamValuesForSearchVertical,
} from './searchVerticalTabsUtils'

interface Props {
  availablePropertyTypes?: Array<App.FilterItem>
  inputsName: string
  selectedPropertyTypes?: Array<string>
  search: string
  onFilterChange: FilterControlChangeHandler
}

function SearchVerticalTabs(props: Props) {
  const {
    availablePropertyTypes = EmptyArray,
    inputsName,
    selectedPropertyTypes = EmptyArray,
    search,
    onFilterChange,
  } = props
  const intl = useIntl()

  const activeTab = useMemo(() => (
    getActiveSearchVerticalTab(selectedPropertyTypes)
  ), [selectedPropertyTypes])

  const allLabel = intl.formatMessage(SEARCH_VERTICAL_TAB_MESSAGES.all)
  const hotelsLabel = intl.formatMessage(SEARCH_VERTICAL_TAB_MESSAGES.hotels)
  const homesLabel = intl.formatMessage(SEARCH_VERTICAL_TAB_MESSAGES.homes)

  const handleValueChange = useCallback((value: SearchVertical) => {
    trackClientEvent({
      subject: `search-vertical-tab-${value}`,
      action: 'clicked',
      category: 'hotels-search-page',
      type: 'interaction',
    })

    const nextSearch = setManySearchParamValues(search, getSearchParamValuesForSearchVertical(value, availablePropertyTypes))

    onFilterChange({ search: nextSearch })
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [availablePropertyTypes, onFilterChange, search])

  return (
    <SegmentedOptionsGroup<SearchVertical>
      inputsName={inputsName}
      distribution="hugged"
      onValueChange={handleValueChange}
    >
      <SegmentedOption
        value="all"
        checked={activeTab === 'all'}
        title={allLabel}
      />
      <SegmentedOption
        value="hotels"
        checked={activeTab === 'hotels'}
        title={hotelsLabel}
      />
      <SegmentedOption
        value="homes"
        checked={activeTab === 'homes'}
        title={homesLabel}
      />
    </SegmentedOptionsGroup>
  )
}

export default SearchVerticalTabs
